import { formatDate } from "@/lib/format";
import { baseUrl } from "@/lib/http";
import { useInfinitePagedList } from "@/lib/useInfinitePagedList";
import { STAGING_STATUS } from "@/types/api";
import type { Staging } from "@/types/api";
import AddIcon from "@mui/icons-material/Add";
import CloseIcon from "@mui/icons-material/Close";
import DeleteIcon from "@mui/icons-material/DeleteOutline";
import EditIcon from "@mui/icons-material/Edit";
import SearchIcon from "@mui/icons-material/Search";
import {
  Box,
  Button,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogContentText,
  DialogTitle,
  IconButton,
  InputAdornment,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TextField,
  Tooltip,
  Typography,
} from "@mui/material";
import { useMutation, useQueryClient } from "@tanstack/react-query";
import { useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { stagingsApi } from "./api";
import { stagingsKeys } from "./queryKeys";
import { StagingStatusFilter } from "./StagingStatusFilter";
import { useStagingsAdminUi } from "./uiStore";

const PAGE_SIZE = 50;

export function StagingsAdminPage() {
  const qc = useQueryClient();
  const nav = useNavigate();
  const {
    rawSearch,
    search,
    statuses,
    deleting,
    previewing,
    setRawSearch,
    setSearch,
    setStatuses,
    setDeleting,
    setPreviewing,
  } = useStagingsAdminUi();

  useEffect(() => {
    const t = setTimeout(() => setSearch(rawSearch), 350);
    return () => clearTimeout(t);
  }, [rawSearch, setSearch]);

  // The status set is part of the query key, so narrowing the filter starts a
  // fresh infinite query rather than appending pages of the previous one.
  const { items, total, isLoading, isFetchingNextPage, sentinelRef } = useInfinitePagedList<
    Staging
  >({
    queryKey: stagingsKeys.list(search, statuses),
    fetchPage: ({ skip, limit }) => stagingsApi.list({ skip, limit, search, statuses }),
    pageSize: PAGE_SIZE,
  });

  const remove = useMutation({
    mutationFn: stagingsApi.remove,
    onSuccess: () => qc.invalidateQueries({ queryKey: stagingsKeys.lists() }),
  });

  return (
    <Box sx={{ p: { xs: 1.5, sm: 2, md: 3 } }}>
      <Paper>
        <Box
          sx={{
            p: { xs: 1.5, sm: 2 },
            display: "flex",
            alignItems: "center",
            flexWrap: "wrap",
            gap: { xs: 1, sm: 2 },
          }}
        >
          <Typography variant="h6" sx={{ flex: 1, minWidth: 140 }}>
            Stagings{" "}
            {!isLoading && (
              <Typography component="span" variant="body2" color="text.secondary">
                ({total})
              </Typography>
            )}
          </Typography>
          <TextField
            size="small"
            placeholder="Search address"
            value={rawSearch}
            onChange={(e) => setRawSearch(e.target.value)}
            sx={{ flex: { xs: 1, sm: "0 0 auto" }, minWidth: 140 }}
            slotProps={{
              input: {
                startAdornment: (
                  <InputAdornment position="start">
                    <SearchIcon fontSize="small" />
                  </InputAdornment>
                ),
                endAdornment: rawSearch
                  ? (
                    <InputAdornment position="end">
                      <IconButton size="small" onClick={() => setRawSearch("")}>
                        <CloseIcon fontSize="small" />
                      </IconButton>
                    </InputAdornment>
                  )
                  : null,
              },
            }}
          />
          <IconButton color="primary" onClick={() => void nav("/administration/stagings/new")}>
            <AddIcon />
          </IconButton>
        </Box>
        <Box sx={{ px: { xs: 1.5, sm: 2 }, pb: { xs: 1.5, sm: 2 } }}>
          <StagingStatusFilter statuses={statuses} onChange={setStatuses} />
        </Box>
        <TableContainer sx={{ overflowX: "auto" }}>
          <Table size="small" sx={{ minWidth: { xs: 720, sm: 0 } }}>
            <TableHead>
              <TableRow>
                <TableCell sx={{ width: 80, display: { xs: "none", sm: "table-cell" } }} />
                <TableCell>ADDRESS</TableCell>
                <TableCell>STATUS</TableCell>
                <TableCell sx={{ display: { xs: "none", md: "table-cell" } }}>START</TableCell>
                <TableCell sx={{ display: { xs: "none", md: "table-cell" } }}>END</TableCell>
                <TableCell sx={{ display: { xs: "none", lg: "table-cell" } }}>NOTES</TableCell>
                <TableCell align="right">ACTIONS</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {isLoading && (
                <TableRow>
                  <TableCell colSpan={7} align="center">
                    <CircularProgress size={20} />
                  </TableCell>
                </TableRow>
              )}
              {items.map((s) => (
                <TableRow key={s.id} hover>
                  <TableCell sx={{ p: 0.5, display: { xs: "none", sm: "table-cell" } }}>
                    {s.imageFile?.thumbnailPath110 && (
                      <Box
                        component="img"
                        src={`${baseUrl}${s.imageFile.thumbnailPath110}`}
                        alt={s.address}
                        onClick={() => setPreviewing(s)}
                        sx={{
                          width: 70,
                          height: 50,
                          objectFit: "cover",
                          borderRadius: 1,
                          cursor: "pointer",
                          display: "block",
                        }}
                      />
                    )}
                  </TableCell>
                  <TableCell>{s.address}</TableCell>
                  <TableCell>
                    {STAGING_STATUS[s.status as keyof typeof STAGING_STATUS] ?? s.status}
                  </TableCell>
                  <TableCell sx={{ display: { xs: "none", md: "table-cell" } }}>
                    {formatDate(s.startDate)}
                  </TableCell>
                  <TableCell sx={{ display: { xs: "none", md: "table-cell" } }}>
                    {formatDate(s.endDate)}
                  </TableCell>
                  <TableCell
                    sx={{ color: "text.secondary", display: { xs: "none", lg: "table-cell" } }}
                  >
                    {s.notes}
                  </TableCell>
                  <TableCell align="right">
                    <Tooltip title="Edit">
                      <IconButton
                        size="small"
                        color="primary"
                        onClick={() => void nav(`/administration/stagings/${s.id}`)}
                      >
                        <EditIcon fontSize="small" />
                      </IconButton>
                    </Tooltip>
                    <Tooltip title="Delete">
                      <IconButton size="small" color="error" onClick={() => setDeleting(s)}>
                        <DeleteIcon fontSize="small" />
                      </IconButton>
                    </Tooltip>
                  </TableCell>
                </TableRow>
              ))}
              {!isLoading && items.length === 0 && (
                <TableRow>
                  <TableCell colSpan={7} align="center">
                    <Typography color="text.secondary">
                      {search || statuses.length > 0
                        ? "No stagings match these filters"
                        : "No stagings"}
                    </Typography>
                  </TableCell>
                </TableRow>
              )}
            </TableBody>
          </Table>
        </TableContainer>
        {isFetchingNextPage && (
          <Box sx={{ display: "flex", justifyContent: "center", p: 1 }}>
            <CircularProgress size={20} />
          </Box>
        )}
        <div ref={sentinelRef} />
      </Paper>

      <Dialog open={deleting !== null} onClose={() => setDeleting(null)}>
        <DialogTitle>Delete staging</DialogTitle>
        <DialogContent>
          <DialogContentText>
            Are you sure you want to delete{" "}
            <strong>{deleting?.address}</strong>? This action cannot be undone.
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDeleting(null)}>Cancel</Button>
          <Button
            color="error"
            variant="contained"
            disabled={remove.isPending}
            onClick={() => {
              if (deleting) remove.mutate(deleting.id, { onSuccess: () => setDeleting(null) });
            }}
          >
            Delete
          </Button>
        </DialogActions>
      </Dialog>

      <Dialog open={previewing !== null} onClose={() => setPreviewing(null)} maxWidth="md">
        {previewing && (
          <>
            <DialogTitle>{previewing.address}</DialogTitle>
            <DialogContent sx={{ p: 0 }}>
              <Box
                component="img"
                src={`${baseUrl}${previewing.imageFile?.relativePath}`}
                alt={previewing.address}
                sx={{ width: "100%", display: "block" }}
              />
            </DialogContent>
            <DialogActions>
              <Button onClick={() => setPreviewing(null)}>Close</Button>
            </DialogActions>
          </>
        )}
      </Dialog>
    </Box>
  );
}
